import { NgStyle } from '@angular/common';
import {
    Component,
    ElementRef,
    OnInit,
    ViewChild,
    forwardRef,
    input,
    output,
    signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Button } from '../button/button.component';
import { Icon } from '../icon/icon.component';

/**
 * Seletor de imagem com prévia, integrável a formulários (`ControlValueAccessor`).
 *
 * Permite escolher um arquivo de imagem (validado contra os formatos em
 * {@link accept}), exibindo uma prévia, e limpá-lo. Emite
 * {@link fileIsNotInAcceptedFormat} quando o formato é inválido e
 * {@link imageCleared} ao remover a imagem.
 */
@Component({
    selector: 'ai-change-image',
    standalone: true,
    templateUrl: './change-image.component.html',
    imports: [Button, Icon, NgStyle],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChangeImage),
            multi: true,
        },
    ],
})
export class ChangeImage implements ControlValueAccessor, OnInit {
    /** Formatos (MIME types) aceitos, ex.: `['image/png','image/jpeg']` (obrigatório). */
    public readonly accept = input.required<string[]>();

    /** Emite quando o arquivo escolhido não está em um formato aceito. */
    public readonly fileIsNotInAcceptedFormat = output<void>();

    /** Emite quando a imagem é removida. */
    public readonly imageCleared = output<void>();

    @ViewChild('fileInput') private fileInput!: ElementRef<HTMLInputElement>;

    protected file: File | null = null;
    protected previewUrl = signal<string | ArrayBuffer | null>(null);
    protected fileFormat = signal<string | null>(null);
    protected isDisabled: boolean = false;
    private onChange: (file: File | null) => void = () => {};
    private onTouched: () => void = () => {};

    ngOnInit(): void {
        this.fileFormat.set(this.getFileFormat(this.file));
    }

    private getFileFormat(file: File | null): string | null {
        if (!file) {
            return null;
        }
        return file.type;
    }

    writeValue(file: File): void {
        if (!file) {
            return;
        }
        this.file = file;
        this.setPreviewUrl(this.file);
    }

    registerOnChange(fn: (value: File | null) => void = () => {}): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void = () => {}): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    onImageFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) {
            return;
        }
        const file: File = input.files[0];

        if (!this.isFileInAcceptedFormat(file)) {
            this.fileIsNotInAcceptedFormat.emit();
            return;
        }
        this.file = file;
        this.fileFormat.set(this.getFileFormat(file));
        this.setPreviewUrl(file);
        this.onChange(this.file);
        const reader = new FileReader();
        reader.readAsDataURL(file);
    }

    private isFileInAcceptedFormat(file: File): boolean {
        const fileFormat = this.getFileFormat(file);
        return this.accept().includes(fileFormat!);
    }

    clear(): void {
        this.file = null;
        this.previewUrl.set(null);
        this.fileFormat.set(null);
        this.fileInput.nativeElement.value = '';
        this.onChange(this.file);
        this.imageCleared.emit();
    }

    private setPreviewUrl(file: File): void {
        const reader = new FileReader();
        reader.onload = () => {
            this.previewUrl.set(
                typeof reader.result === 'string'
                    ? reader.result
                          .replace('image/svg', 'image/svg+xml')
                          .replace('image/svg+xml+xml', 'image/svg+xml')
                    : reader.result,
            );
        };
        reader.readAsDataURL(file);
    }
}
